import React, { useCallback, useEffect, useMemo, useState } from "react";
import cx from "classnames";
import { Hooks, Util } from "@xt/xtbase";
const { useTranslation } = Hooks;
const { Big } = Util;
import { Slider } from "antd";
import {
  normalizeAicopyBalanceRaw,
  resolveSliderAmountDecimals,
} from "../../utils/aicopyCurrencies";
import styles from "./index.module.less";

function TradeSlider({ className, value, max, point = 0, disabled, onChange, noBtnAll = false }) {
  const t = useTranslation();
  const [sliderValue, setSliderValue] = useState(+(value || 0) || 0);
  const lastVibrateAtRef = React.useRef(0);
  const maxStr = useMemo(() => String(max ?? "").replace(/,/g, "").trim(), [max]);

  // 设计稿：0/25/50/75/100 五档节点（rc-slider 会在每个 mark 位置渲染圆点）
  const percentMarks = useMemo(
    () => ({
      0: <span className={styles.markLabel}>0%</span>,
      25: <span className={styles.markLabel}>25%</span>,
      50: <span className={styles.markLabel}>50%</span>,
      75: <span className={styles.markLabel}>75%</span>,
      100: <span className={styles.markLabel}>100%</span>,
    }),
    []
  );

  const tooltipFormatter = useCallback((val) => {
    return val + "%";
  }, []);
  const triggerVibrate = useCallback(() => {
    if (typeof window === "undefined") return;
    const { navigator } = window;
    if (!navigator || typeof navigator.vibrate !== "function") return;
    const now = Date.now();
    if (now - lastVibrateAtRef.current < 80) return;
    lastVibrateAtRef.current = now;
    navigator.vibrate(10);
  }, []);

  /** 手柄与端点圆点重叠时隐藏该点，避免 0%/100% 双层圆 */
  const dotStyle = useCallback(
    (dotValue) => {
      if (dotValue === 100 && sliderValue === 100) {
        return { opacity: 0, visibility: "hidden", pointerEvents: "none" };
      }
      if (dotValue === 0 && sliderValue === 0) {
        return { opacity: 0, visibility: "hidden", pointerEvents: "none" };
      }
      return {};
    },
    [sliderValue]
  );

  const handleChange = useCallback(
    (val) => {
      triggerVibrate();
      setSliderValue(val);
      if (!maxStr || +maxStr === 0) {
        onChange && onChange("0", val);
        return;
      }
      if (val === 100) {
        onChange && onChange(normalizeAicopyBalanceRaw(maxStr), val);
        return;
      }
      const decimals = resolveSliderAmountDecimals(maxStr, point || 2);
      const amount = normalizeAicopyBalanceRaw(
        Big(maxStr).times(val).div(100).toFixed(decimals)
      );
      onChange && onChange(amount, val);
    },
    [maxStr, point, onChange, triggerVibrate]
  );

  useEffect(() => {
    if (!value) return setSliderValue(0);
    const valueStr = String(value ?? "").replace(/,/g, "").trim();
    const valueNum = +(valueStr || 0) || 0;
    const maxNum = +(maxStr || 0) || 0;

    if (!maxNum) {
      if (valueNum) {
        setSliderValue(100);
      }
    } else if (Big(valueStr || 0).gte(maxStr || 0)) {
      setSliderValue(100);
    } else {
      setSliderValue(Math.round((valueNum / maxNum) * 100));
    }
  }, [value, maxStr]);

  return (
    <div className={cx(styles.main, className)}>
      <Slider
        value={sliderValue}
        tooltip={{ formatter: tooltipFormatter }}
        onChange={handleChange}
        disabled={disabled}
        marks={percentMarks}
        dots={false}
        dotStyle={dotStyle}
        step={1}
        max={100}
        min={0}
      />
      {!noBtnAll && (
        <button disabled={disabled} className={cx("btnTxt btnHover", styles.btnAll)} onClick={() => handleChange(100)}>
          {t("trade.all")}
        </button>
      )}
    </div>
  );
}

export default TradeSlider;
